import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {Box, Divider, IconButton, Input, Slide, Stack, Typography} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import {SupervisionHistoryItem} from "./supervision-history-item";
import {HugeiconsIcon} from "@hugeicons/react";
import {SendIcon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../../../context/SignInContext";
import {grey} from "@mui/material/colors";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";
import {useFlowProtection} from "../../../../hooks/useFlowProtection";

export const SupervisionConversationHistory = ({selectedConversation, setSelectedConversation, debug, project, conversationType = "CHAT_BOT", playerSlot = null, bottomSlot = null}) => {
  const {currentUser} = useContext(SignInContext);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 3,
    totalPages: 1,
    totalElements: 1
  });
  const [messages, setMessages] = useState([]);
  const [previous, setPrevious] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [message, setMessage] = useState("");
  const scrollRef = useRef(null);
  const messagesAbortController = useRef(null);

  const [updatedValue] = useStompWebsocket(
    selectedConversation?.status === "ACTIVE" ?
      `/topic/session/${selectedConversation?.id}` : null
  );
  const {token: protectionToken, extend: extendProtection} = useFlowProtection();

  // the latest fetchHistory call wins (it is reloaded when the protection token or the sender changes)
  const historyRequestRef = useRef(0);
  const fetchHistory = async (page) => {
    if (!selectedConversation?.id) return;
    const requestId = ++historyRequestRef.current;
    try {
      const response = await ConversationRepository.getHistory({
        id: selectedConversation.id - 1,
        caller: selectedConversation.sender,
        type: selectedConversation?.type,
        page,
        size: 3
      });

      if (requestId !== historyRequestRef.current) return;
      const newData = response.data.content;

      setHistory(prev =>
        page === 0
          ? newData
          : [
            ...prev,
            ...newData.filter(
              newItem =>
                !prev.some(
                  prevItem => prevItem.conversation.id === newItem.conversation.id
                )
            )
          ]
      );

      setPagination({
        page,
        size: pagination.size,
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements
      });

      setHasMore(page + 1 < response.data.totalPages);
    } catch (error) {
      console.error("Failed to fetch conversation history", error);
    }
  };

  const loadMoreIfNeeded = () => {
    if (pagination.page + 1 < pagination.totalPages) {
      fetchHistory(pagination.page + 1);
    }
  };

  // the conversation this one continues (previousConversationId), shown above it; only one level
  const previousId = selectedConversation?.previousConversationId;
  useEffect(() => {
    setPrevious(null);
    if (!previousId) return;
    let cancelled = false;
    Promise.all([ConversationRepository.getConversation({id: previousId}), ConversationRepository.getMessages(previousId)])
      .then(([conversationResponse, messagesResponse]) => {
        if (!cancelled) setPrevious({conversation: conversationResponse.data, messages: messagesResponse.data || []});
      })
      .catch(error => console.error("Failed to fetch the previous conversation", error));
    return () => {
      cancelled = true;
    };
  }, [previousId]);
  const showPrevious = previous && !history.some(entry => entry?.conversation?.id === previous.conversation?.id);

  const scrollBottom = () => {
    const bottom = document.getElementById("bottom-message");
    if (bottom) {
      bottom.scrollIntoView({behavior: "smooth", block: "end"});
    }
  };

  useEffect(() => {
    if (selectedConversation?.id) {
      setHistory([]);
      setPagination({page: 0, size: 2, totalPages: 1, totalElements: 1});
      setHasMore(true);
      fetchHistory(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id, selectedConversation?.sender, protectionToken]);

  useEffect(() => {
    if (history.length && pagination.page === 0) {
      setTimeout(() => scrollBottom(), 500);
    }
  }, [history, pagination.page]);

  useEffect(() => {
    if (selectedConversation?.id) {
      fetchMessages();
    }
  }, [updatedValue, selectedConversation, protectionToken]);

  const fetchMessages = () => {
    // Abort previous request if it exists
    if (messagesAbortController.current) {
      messagesAbortController.current.abort();
    }

    const controller = new AbortController();
    messagesAbortController.current = controller;
    ConversationRepository.getMessages(selectedConversation.id, controller.signal)
      .then(response => {
        setMessages(response?.data || []);
        if (protectionToken) extendProtection();
      })
      .catch(error => {
        if (error.name !== "AbortError") {
          console.log("Failed to fetch messages:", error);
        }
      })
  }

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!message) {
      return;
    }
    const historyItem = history?.find(i => i?.conversation?.id === selectedConversation?.id);
    const conv = historyItem?.conversation || null;
    const isEndMessage = historyItem?.messages?.some(
      item => (item?.transfer != null && item.transfer !== '') || item?.end_message === true
    );
    if (conv && (conv?.status !== "ACTIVE" || isEndMessage)) {
      const username = currentUser?.username;
      const sender = project?.vdnManager ? project?.vdnManager[username] || -999 : -999;

      ConversationRepository.createConversation({
        flowUid: project?.id,
        sender: sender,
        message: message,
        type: conversationType
      })
        .then((response) => {
          setSelectedConversation(response?.data);
          setMessage("");
        })
        .catch(error => {
          console.log(error)
        })
      return;
    }

    ConversationRepository.sendMessage({
      id: selectedConversation?.id,
      message: message,
    })
      .then((response) => {
        setMessage("");
        const conversation = response?.data;
        setHistory(prev => prev?.map(i => i?.conversation?.id === conversation?.id ? {...i, conversation} : {...i}));
      })
      .catch(error => {
        console.log(error)
      })
  }

  const isFinished = useMemo(() => {
    const historyItem = history?.find(i => i?.conversation?.id === selectedConversation?.id);
    const conv = historyItem?.conversation || null;
    const isEndMessage = historyItem?.messages?.some(
      item => (item?.transfer != null && item.transfer !== '') || item?.end_message === true
    );
    return conv && (conv?.status !== "ACTIVE" || isEndMessage) || false;
  }, [history, selectedConversation?.id])

  const canUserWrite = useMemo(() => {
    const agentMessages = messages?.filter(m => m?.role === "AGENT");
    const msg = agentMessages?.length > 0 ? agentMessages[agentMessages.length - 1] : null;

    return msg !== null && msg?.last_message === true;
  }, [messages])

  return (
    <Box sx={{position: "relative", height: "100%"}}>
      <div
        id="scrollableDiv"
        ref={scrollRef}
        style={{
          height: "calc(100% - 2px)",
          overflow: "auto",
          display: "flex",
          flexDirection: "column-reverse"
        }}
      >
        <InfiniteScroll
          dataLength={history.length}
          next={loadMoreIfNeeded}
          hasMore={hasMore}
          inverse={true}
          scrollableTarget="scrollableDiv"
          style={{display: "flex", flexDirection: "column-reverse"}}
          loader={
            <Box p={2} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Loading more...
              </Typography>
            </Box>
          }
        >

          <Box id="bottom-message" sx={{pb: selectedConversation?.type === "CHAT_BOT" ? 20 : 5}}/>
          <SupervisionHistoryItem
            conversation={selectedConversation}
            messages={messages?.sort((a, b) => a.id - b.id)}
            project={project}
            loadDuration={true}
            showDebug={debug}
            showUserTalking={true}
            playerSlot={playerSlot}
            updateConversation={(conversation) => setSelectedConversation(conversation)}
            fetchMessages={fetchMessages}
            extraActions={bottomSlot}
          />
          {isFinished ?
            <Divider
              sx={{
                width: "90%",
                mx: "auto",
                textTransform: "capitalize",
                fontSize: "0.85em",
                color: grey[600],
                fontWeight: 500
              }}
            >
              Conversation Ended
            </Divider> :
            null
          }
          {showPrevious && (
            <>
              <Divider sx={{width: "90%", mx: "auto", my: 1, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#4B55C9"}}>
                CURRENT CONVERSATION #{selectedConversation?.id}
              </Divider>
              <Box sx={{
                mx: 1.5, my: 1, borderRadius: "16px", bgcolor: "rgba(132, 145, 247, 0.08)",
              }}>
                <Typography sx={{px: 2.5, pt: 1.5, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#8491F7"}}>
                  PREVIOUS CONVERSATION #{previous.conversation?.id}
                </Typography>
                <Box sx={{opacity: 0.85}}>
                  <SupervisionHistoryItem
                    conversation={previous.conversation}
                    messages={[...previous.messages].sort((a, b) => a.id - b.id)}
                    project={project}
                    loadDuration={false}
                    showDebug={debug}
                    updateConversation={() => {}}
                    showUserTalking={false}
                    fetchMessages={() => {}}
                  />
                </Box>
              </Box>
            </>
          )}
          {history.map((entry) => {
            const messages = entry?.messages?.sort((a, b) => a.id - b.id) || [];
            const conversation = entry?.conversation;

            return (
              <SupervisionHistoryItem
                key={conversation?.id}
                conversation={conversation}
                messages={messages}
                project={project}
                loadDuration={false}
                showDebug={debug}
                updateConversation={(conversation) => setSelectedConversation(conversation)}
                showUserTalking={false}
                fetchMessages={fetchMessages}
              />
            );
          })}

        </InfiniteScroll>
      </div>
      {
        selectedConversation?.type === "CHAT_BOT" &&
        <Slide in={selectedConversation !== null} direction={"up"} timeout={700}>
          <Box sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(to top, white, rgba(255,255,255,0.7), transparent)"
          }}>
            <Stack component={"form"} onSubmit={handleSubmit} maxWidth={"sm"} direction={"row"} gap={2}
                   className={"border-shadow"} sx={{
              p: 2,
              borderRadius: 5,
              width: "90%",
              mb: 5,
              mx: "auto",
              bgcolor: "background.paper",
              zIndex: 10000
            }}>
              <Input
                placeholder={"Enter your message here"} disableUnderline
                sx={{flex: 1}} maxRows={3} multiline
                value={message}
                disabled={!canUserWrite}
                autoFocus
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault(); // prevent newline
                    handleSubmit(e);    // manually submit
                  }
                }}
              />
              <Box>
                <IconButton type={"submit"}><HugeiconsIcon icon={SendIcon}/></IconButton>
              </Box>
            </Stack>
          </Box>
        </Slide>
      }
    </Box>
  );
};
